import styled from 'styled-components';
import shipImg from '../assets/ship.jpg';
import { colors, fonts } from '../theme';
import { ApplyButton, StarFlareIcon } from './HudFrame';

// Sticky so anchor-nav links have a stable target to scroll under —
// SectionAnchor's scroll-margin-top in App.tsx matches this bar's height.
const Bar = styled.nav`
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.1rem 2rem;
  border-bottom: 1px solid ${colors.hairline};
  background: rgba(5, 7, 13, 0.55);
  backdrop-filter: blur(6px);

  @media (max-width: 768px) {
    padding: 0.85rem 1.1rem;
    flex-wrap: wrap;
    row-gap: 0.75rem;
  }
`;

const Emblem = styled.img`
  width: 28px;
  height: 28px;
  object-fit: cover;
  border-radius: 50%;
  border: 1px solid ${colors.amberDim};
  background: #000;
  filter: drop-shadow(0 0 6px rgba(242, 169, 59, 0.35));
  transition: filter 0.3s ease;

  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
  }
`;

const Brand = styled.a`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  text-decoration: none;
  color: ${colors.textPrimary};
  cursor: pointer;

  &:hover ${Emblem} {
    filter: drop-shadow(0 0 10px rgba(242, 169, 59, 0.6));
  }
`;

const BrandText = styled.span`
  font-family: ${fonts.display};
  font-size: 0.85rem;
  letter-spacing: 0.18em;
  color: ${colors.textPrimary};

  @media (max-width: 480px) {
    display: none;
  }
`;

// Groups the anchor links and the Interest Form button on the right side of the
// bar, opposite Brand — Bar's own justify-content: space-between only has
// two top-level children (Brand and this), so the button doesn't end up
// stranded in the middle if a third top-level child were added instead.
const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-family: ${fonts.mono};

  @media (max-width: 768px) {
    gap: 0.15rem;
    flex-wrap: wrap;
  }
`;

const NavLink = styled.a`
  color: ${colors.textDim};
  text-decoration: none;
  font-size: 0.78rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  transition: color 0.25s ease;
  position: relative;
  padding: 0.5rem 0.85rem;
  cursor: pointer;

  @media (max-width: 768px) {
    font-size: 0.68rem;
    padding: 0.4rem 0.6rem;
    letter-spacing: 0.1em;
  }

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 1px;
    bottom: 0.1rem;
    left: 0.85rem;
    right: 0.85rem;
    background-color: ${colors.amber};
    transition: width 0.25s ease;
  }

  &:hover {
    color: ${colors.amber};
    &:after {
      width: calc(100% - 1.7rem);
    }
  }
`;

// Smooth-scrolls to a section by id rather than relying solely on the
// native `href="#id"` jump, so behavior is consistent even if the browser's
// own smooth-scroll CSS support is unavailable.
function scrollToId(id: string) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

export function Navigation() {
  return (
    <Bar aria-label="Primary">
      <Brand
        href="#top"
        aria-label="Mission Control home"
        onClick={(e) => {
          e.preventDefault();
          scrollToTop();
        }}
      >
        <Emblem src={shipImg} alt="Mission Control rocket emblem" />
        <BrandText>MISSION CONTROL</BrandText>
      </Brand>

      <NavRight>
        <NavLinks>
          <NavLink
            href="#top"
            onClick={(e) => {
              e.preventDefault();
              scrollToTop();
            }}
          >
            Home
          </NavLink>
          <NavLink
            href="#about"
            onClick={(e) => {
              e.preventDefault();
              scrollToId('about');
            }}
          >
            About
          </NavLink>
          <NavLink
            href="#community"
            onClick={(e) => {
              e.preventDefault();
              scrollToId('community');
            }}
          >
            Community
          </NavLink>
        </NavLinks>

        <ApplyButton
          $compact
          href="https://docs.google.com/forms/d/e/1FAIpQLScg3rlx0zEP-Fz7aTy-soqKH4oywIDwSokcCa8C-TQkQBXAhg/viewform?usp=sharing&ouid=101033723206810482399"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Mission Control interest form"
        >
          <StarFlareIcon />
          Interest Form
        </ApplyButton>
      </NavRight>
    </Bar>
  );
}
